import { ReactNode } from 'react';
import { Controller, useFormContext, useWatch } from 'react-hook-form';
import { MenuItem, TextField } from '@client/new/components/basic';
import { CollapsibleSection } from '../primitives/CollapsibleSection';
import { InfoTooltip } from '../primitives/InfoTooltip';
import { useSectionOpenState } from '../../lib/useSectionOpenState';
import * as styles from './StructureSection.css';
import {
  ProgramFormValues,
  PUBLISHING_BEHAVIOUR_OPTIONS,
  STRUCTURE_OPTIONS,
} from '../../model/programForm.types';

type StructureSectionProps = {
  isSubmitting?: boolean;
};

const STRUCTURE_TOOLTIP = (
  <>
    <p>
      Workout structure refers to the organized framework that outlines how
      single workouts are arranged within a program.
    </p>
    <p>
      Series - Series are chronologically ordered videos, but don&apos;t have
      any time frame attached to them.
    </p>
    <p>
      Week/day - Daily programs are assigned to specific days (i.e., Day 1, Day
      2). They are also labeled as such in the app.
    </p>
    <p>
      Category - A category allows you to bucket one-off workouts into
      easy-to-navigate descriptions. It&apos;s organized around a specific body
      part (or theme) and is tagged and bucketed under that tag.
    </p>
  </>
);

const PUBLISHING_BEHAVIOUR_TOOLTIP = (
  <p>
    This is the order in which new workouts you add appear in the program on the
    consumer app.
  </p>
);

const WORKOUT_LIMIT_TOOLTIP = (
  <p>
    This field allows you to limit the number of workouts that can be published
    to a category. If you set the limit to 15 workouts, once you publish workout
    16, the oldest workout will be archived to maintain the set 15 workout
    limit.
  </p>
);

const labelWithTooltip = (
  text: string,
  ariaLabel: string,
  tooltip: ReactNode,
): ReactNode => (
  <span className={styles.labelWithTooltip}>
    {text}
    <InfoTooltip ariaLabel={ariaLabel}>{tooltip}</InfoTooltip>
  </span>
);

export const StructureSection = ({ isSubmitting }: StructureSectionProps) => {
  const { control, setValue } = useFormContext<ProgramFormValues>();
  const { isOpen, setIsOpen } = useSectionOpenState();

  const contentStructure = useWatch({ control, name: 'contentStructure' });
  const isCategory = contentStructure === 'category';

  return (
    <CollapsibleSection
      title="Structure"
      isOpen={isOpen}
      onOpenChange={setIsOpen}
    >
      <div className={styles.subtitle}>
        How workouts will be structured and shown in the consumer app
      </div>
      <div className={styles.row}>
        <div className={styles.select}>
          <TextField
            fullWidth
            select
            size="small"
            label={labelWithTooltip(
              'Structure',
              'Structure information',
              STRUCTURE_TOOLTIP,
            )}
            value={contentStructure}
            onChange={(e) => {
              const next = STRUCTURE_OPTIONS.find(
                (o) => o.value === e.target.value,
              );
              if (next)
                setValue('contentStructure', next.value, { shouldDirty: true });
            }}
            disabled={isSubmitting}
            data-testid="program-form-structure"
          >
            {STRUCTURE_OPTIONS.map((option) => (
              <MenuItem key={option.value} value={option.value}>
                {option.label}
              </MenuItem>
            ))}
          </TextField>
        </div>

        <div className={styles.publishingSelect}>
          <Controller
            control={control}
            name="publishingBehaviour"
            render={({ field }) => (
              <TextField
                {...field}
                fullWidth
                select
                size="small"
                label={labelWithTooltip(
                  'Publishing behaviour',
                  'Publishing behaviour information',
                  PUBLISHING_BEHAVIOUR_TOOLTIP,
                )}
                disabled={isSubmitting}
                data-testid="program-form-publishing-behaviour"
              >
                {PUBLISHING_BEHAVIOUR_OPTIONS.map((option) => (
                  <MenuItem key={option.value} value={option.value}>
                    {option.label}
                  </MenuItem>
                ))}
              </TextField>
            )}
          />
        </div>

        {isCategory ? (
          <div className={styles.workoutLimit}>
            <Controller
              control={control}
              name="workoutLimit"
              render={({ field }) => (
                <TextField
                  {...field}
                  fullWidth
                  size="small"
                  label={labelWithTooltip(
                    'Workout limit',
                    'Workout limit information',
                    WORKOUT_LIMIT_TOOLTIP,
                  )}
                  placeholder="Set number"
                  disabled={isSubmitting}
                  type="number"
                  data-testid="program-form-workout-limit"
                />
              )}
            />
          </div>
        ) : null}
      </div>
    </CollapsibleSection>
  );
};
